<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { INPUT_NOT_DEFINED } from "~/modules/dtwin/constants";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinDescriptionGeolocation } from "~/modules/dtwin/models/DtwinDescriptionGeolocation.interface";
import type { DtwinDescriptionIdCard } from "~/modules/dtwin/models/DtwinDescriptionIdCard.interface";
import type { DynamicUIGroup } from "~/modules/dtwin/models/DynamicUIGrouped.interface";
import dtwinsService from "~/modules/dtwin/services/dtwin.service";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

interface Props {
  dtwin?: Dtwin | null;
  deviceModelUiConfig?: DynamicUIGroup;
  isLoadingModelUiConfig?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits(["update:dtwin"]);

const { t } = useI18n();
const acl = useAcl();

const isAllowedToEdit = computed(() => acl.can(ACLRoles.CAN_WRITE_DEVICE));

const { currentOrganization } = storeToRefs(useOrganizationStore());

/**
 * ID card representation used in the device ID UI card.
 */
const idCard: Ref<DtwinDescriptionIdCard> = ref({
  friendlyName: props.dtwin?.attributes.friendlyName,
  doesUpdateSucceed: false,
});

/**
 * Handle the device friendly name update required by the user.
 * @param friendlyName New device friendly name
 */
async function updateFriendlyName(friendlyName: string): Promise<void> {
  const doesUpdateSucceed = await onUpdate({
    data: { friendlyName },
  });
  if (doesUpdateSucceed) {
    (props.dtwin as Dtwin).attributes.friendlyName = friendlyName;
    emit("update:dtwin", props.dtwin);
  }

  idCard.value = {
    friendlyName: doesUpdateSucceed
      ? friendlyName
      : props.dtwin?.attributes.friendlyName,
    doesUpdateSucceed,
  };
}

/**
 * Geolocation representation used in the geolocation UI card.
 */
const geolocation: Ref<DtwinDescriptionGeolocation> = ref({
  address: props.dtwin?.attributes?.address ?? INPUT_NOT_DEFINED,
  doesUpdateSucceed: false,
});

/**
 * Handle the geolocation address update required by the user.
 * @param address New device address
 */
async function updateGeolocationAddress(address: string): Promise<void> {
  const doesUpdateSucceed = await onUpdate({
    data: { address },
  });
  if (doesUpdateSucceed) {
    (props.dtwin as Dtwin).attributes.address = address;
    emit("update:dtwin", props.dtwin);
  }

  geolocation.value = {
    address: doesUpdateSucceed ? address : props.dtwin?.attributes.address,
    doesUpdateSucceed,
  };
}

async function onUpdate({
  data,
}: {
  data: Record<string, unknown>;
}): Promise<boolean> {
  if (!currentOrganization.value?.code) {
    throw new Error("Organization code is undefined");
  }
  if (props.dtwin?.uid) {
    const response = await dtwinsService.patchOrganizationDtwin(
      currentOrganization.value.code,
      props.dtwin.uid,
      data,
    );

    if (!LxcError.check(response)) {
      emit("update:dtwin", props.dtwin);
      showNotificationSuccess(t(NotificationKey.saveSuccess));
      return true;
    }
    response.notify(NotificationKey.saveError);
  }
  return false;
}
</script>

<template>
  <dtwin-form-description-id-card
    v-if="dtwin && deviceModelUiConfig?.details?.description?.idCard"
    :model-value="idCard"
    :is-allowed-to-edit="isAllowedToEdit"
    :dtwin="dtwin"
    :device-model-ui-config="deviceModelUiConfig?.details?.description?.idCard"
    @update:name="updateFriendlyName"
  />
  <dtwin-form-description-state
    v-if="dtwin && deviceModelUiConfig?.details?.description?.state"
    :dtwin="dtwin"
    :device-model-ui-config="deviceModelUiConfig?.details?.description?.state"
  />
  <dtwin-form-description-connectivity
    v-if="deviceModelUiConfig?.details?.description?.connectivity"
    :dtwin="dtwin"
    :device-model-ui-config="
      deviceModelUiConfig.details.description.connectivity
    "
  />
  <dtwin-form-description-geolocation
    v-if="dtwin && deviceModelUiConfig?.details?.description?.geolocation"
    class="mb-4"
    :dtwin="dtwin"
    :model-value="geolocation"
    :is-allowed-to-edit="isAllowedToEdit"
    :device-model-ui-config="deviceModelUiConfig"
    @update:address="updateGeolocationAddress"
  />
</template>
